/**
 * Users management page (gamemaster self-service).
 *
 * Lets a gamemaster (or admin) manage the users of their own account: add,
 * edit info/role, and deactivate/reactivate ("delete" is a soft deactivation).
 * The list is rendered with the shared `UsersTable`, so improvements there are
 * shared with the admin Accounts page. Access is gated to gamemaster/admin in
 * addition to the backend authorization.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers,
    faPlus,
    faExclamationTriangle,
    faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import { RootState, AppDispatch } from '../store';
import { fetchUsers, updateUser, clearUsersError } from '../store/usersSlice';
import { User, UserRole } from '../types/auth';
import UsersTable from '../components/users/UsersTable';
import UserFormModal from '../components/users/UserFormModal';

// Gamemasters may assign client/gamemaster only — not admin (a global operator
// role). Module-level so the reference is stable across renders.
const GM_ROLE_OPTIONS: UserRole[] = ['client', 'gamemaster'];

const UsersPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();

    const { users, isLoading, error, mutatingIds } = useSelector(
        (state: RootState) => state.users
    );
    const userRole = useSelector((state: RootState) => state.auth.user?.role);
    const currentUserId = useSelector((state: RootState) => state.auth.user?.id);
    const canManage = userRole === 'gamemaster' || userRole === 'admin';

    const [showForm, setShowForm] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);

    useEffect(() => {
        if (canManage) {
            dispatch(fetchUsers());
        }
    }, [canManage, dispatch]);

    const handleAddClick = useCallback(() => {
        setEditingUser(null);
        setShowForm(true);
    }, []);

    const handleEditClick = useCallback((user: User) => {
        setEditingUser(user);
        setShowForm(true);
    }, []);

    const handleToggleActive = useCallback(
        (user: User) => {
            if (
                user.is_active &&
                !window.confirm(
                    `Deactivate ${user.username}? They will no longer be able to sign in until reactivated.`
                )
            ) {
                return;
            }
            dispatch(updateUser({ userId: user.id, data: { is_active: !user.is_active } }));
        },
        [dispatch]
    );

    // Role gate — mirror the nav gating so a direct navigation is also blocked.
    if (!canManage) {
        return (
            <Container fluid className="h-100 d-flex align-items-center justify-content-center">
                <Row>
                    <Col className="text-center text-muted">
                        <FontAwesomeIcon icon={faUserShield} size="4x" className="mb-3" />
                        <h3>Users</h3>
                        <p>Only a gamemaster can manage users.</p>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container fluid className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3>
                        <FontAwesomeIcon icon={faUsers} className="me-2" />
                        Users
                    </h3>
                    <p className="text-muted mb-0">
                        Manage the users in your account and their access.
                    </p>
                </div>
                <Button variant="primary" onClick={handleAddClick}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add User
                </Button>
            </div>

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => dispatch(clearUsersError())}
                >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {error}
                </Alert>
            )}

            {isLoading && users.length === 0 ? (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading users...</span>
                    </Spinner>
                </div>
            ) : (
                <UsersTable
                    users={users}
                    loading={false}
                    currentUserId={currentUserId}
                    mutatingIds={mutatingIds}
                    onEditUser={handleEditClick}
                    onToggleActive={handleToggleActive}
                    showStatus
                    emptyText="No users yet. Add one to get started."
                />
            )}

            <UserFormModal
                show={showForm}
                onHide={() => setShowForm(false)}
                user={editingUser}
                roleOptions={GM_ROLE_OPTIONS}
            />
        </Container>
    );
};

export default UsersPage;
